import React, { useState, useEffect } from 'react'
import SearchBar from './search-bar'
import { makeStyles } from '@material-ui/styles'
import EnhancedTable from 'components/tables/enhanced-table'
import PermContactCalendarOutlinedIcon from '@material-ui/icons/PermContactCalendarOutlined'
import EmptyStudents from 'components/empty-page-state'
import { LinearProgress, TableContainer } from '@material-ui/core'
import { LeadTableItem } from './lead-table-item'
import { useIsTablet } from 'lib/hooks'
import withSearch from './with-search'
import DisclaimerWithTwoCta from 'components/disclaimer-with-two-cta'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import Button from '@material-ui/core/Button'
import { useUpgradeFlow } from 'lib/hooks/use-upgrade-flow'
import UpgradeSuccessModal from 'components/upgrade-success-modal'

export const useStyles = makeStyles(theme => ({
  leadNameCell: {
    paddingLeft: '1.5rem',
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
  containerStyles: {
    boxSizing: 'border-box',
    padding: '0 36px 1rem',
    maxWidth: '100%',
    marginTop: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(2),
      marginBottom: '60px',
      padding: '0 8px 1rem',
    },
  },
}))

const LeadsTable = ({
  filteredItems,
  setUnviewedLeadCount,
  refetch,
  studentRefetch,
  freeTierLimitsReached,
  isLeadAgeAndMessagingUIEnabled = false,
}) => {
  const isTablet = useIsTablet()
  const classes = useStyles()
  const [filteredLeads, setFilteredLeads] = useState(filteredItems)
  const [searchTerm, setSearchTerm] = useState('')
  const isLeadsLimitReached = freeTierLimitsReached.includes('LEADS')
  const [showDisclaimer, setShowDisclaimer] = useState(isLeadsLimitReached)

  const {
    upgradeLoading: loading,
    handleUpgradeClick,
    showSuccessModal,
    handleDismiss,
  } = useUpgradeFlow('leads')

  // true if search term is empty - '' - or contained by name; false otherwise
  const nameMatchesSearchToken = ({ firstName, lastName }, token) => {
    const fullName = `${firstName} ${lastName}`.toLowerCase().trim()
    const term = token.toLowerCase().trim()

    return fullName.includes(term)
  }

  // inside the call to searchableItems.filter, return cond && otherCond && ... for filtering purposes
  //
  // In doing this we can compose filters.
  //
  // Make sure to update the effect dependencies list.
  useEffect(() => {
    const filtered = filteredItems.filter(item =>
      nameMatchesSearchToken(item, searchTerm),
    )
    setFilteredLeads(filtered)
  }, [searchTerm, filteredItems])

  const sortByCreatedOn = (a, b, dir) => {
    a = a.props.lead.createdAt
    b = b.props.lead.createdAt

    if (a < b) return dir === 'desc' ? 1 : -1
    if (a === b) return 0
    if (a > b) return dir === 'desc' ? -1 : 1
  }

  const sortByFullName = (a, b, dir) => {
    a = (
      a.props.lead.firstName.toLowerCase() + a.props.lead.lastName.toLowerCase()
    ).replace(/\s/g, '')
    b = (
      b.props.lead.firstName.toLowerCase() + b.props.lead.lastName.toLowerCase()
    ).replace(/\s/g, '')

    if (a > b) return dir === 'desc' ? 1 : -1
    if (a < b) return dir === 'desc' ? -1 : 1
    return 0
  }

  const columns = [
    {
      id: 'name',
      label: 'Name',
      align: 'left',
      width: isTablet ? '80%' : '30%',
      mobile: true,
      sortable: true,
      sortComparator: sortByFullName,
    },
    {
      id: 'interest',
      label: 'Interest',
      align: 'left',
      width: '30%',
      mobile: false,
      sortable: false,
    },
    {
      id: 'created-on',
      label: 'Created On',
      align: 'left',
      width: '20%',
      mobile: false,
      sortable: true,
      sortComparator: sortByCreatedOn,
    },
    {
      id: 'actions',
      label: '',
      align: 'right',
      width: '20%',
      mobile: true,
      sortable: false,
    },
  ]

  const rows = filteredLeads?.map(lead => (
    <LeadTableItem
      lead={lead}
      key={`${lead.id}`}
      setUnviewedLeadCount={setUnviewedLeadCount}
      refetch={refetch}
      studentRefetch={studentRefetch}
    />
  ))

  const awaitingResponseLeads = isLeadAgeAndMessagingUIEnabled
    ? filteredLeads.filter(lead => !lead.firstMessagedAt)
    : []
  const contactedLeads = isLeadAgeAndMessagingUIEnabled
    ? filteredLeads.filter(lead => !!lead.firstMessagedAt)
    : []

  if (isLeadAgeAndMessagingUIEnabled) {
    return (
      <>
        <TableContainer className={classes.containerStyles}>
          {loading && <LinearProgress color="secondary" />}
          <DisclaimerWithTwoCta
            icon={<InfoOutlinedIcon style={{ color: '#554427' }} />}
            description="Enjoy the benefits of your free account with up to two leads. Want more? Upgrade now to receive unlimited coaching requests."
            leftCta={
              <Button
                variant="text"
                color="primary"
                onClick={handleUpgradeClick}
                style={{ paddingLeft: 0 }}
              >
                Upgrade Now
              </Button>
            }
            rightCta={
              <Button
                variant="text"
                color="primary"
                onClick={() => {
                  setShowDisclaimer(false)
                }}
              >
                Got It
              </Button>
            }
            show={showDisclaimer}
          />
          <SearchBar
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            placeholder="Search Leads"
            data-testid="leads-list-search"
          />
          <div style={{ marginTop: 32 }}>
            <h3>Awaiting Response ({awaitingResponseLeads.length})</h3>
            {awaitingResponseLeads.length === 0 ? (
              <EmptyStudents
                title="Leads"
                caption="No leads awaiting response."
                IconComp={() => (
                  <PermContactCalendarOutlinedIcon fontSize="large" />
                )}
                removeButton
              />
            ) : (
              <EnhancedTable
                rows={awaitingResponseLeads.map(lead => (
                  <LeadTableItem
                    lead={lead}
                    key={lead.id}
                    setUnviewedLeadCount={setUnviewedLeadCount}
                    refetch={refetch}
                    studentRefetch={studentRefetch}
                    showRespondButton
                    showRelativeTime
                  />
                ))}
                columns={[
                  {
                    id: 'name',
                    label: 'Name',
                    align: 'left',
                    width: isTablet ? '80%' : '30%',
                    mobile: true,
                    sortable: true,
                    sortComparator: sortByFullName,
                  },
                  {
                    id: 'interest',
                    label: 'Interest',
                    align: 'left',
                    width: '30%',
                    mobile: false,
                    sortable: false,
                  },
                  {
                    id: 'submitted',
                    label: 'Submitted',
                    align: 'left',
                    width: '20%',
                    mobile: false,
                    sortable: true,
                    sortComparator: sortByCreatedOn,
                  },
                  {
                    id: 'actions',
                    label: '',
                    align: 'right',
                    width: '20%',
                    mobile: true,
                    sortable: false,
                  },
                ]}
                label="Awaiting Response"
                numberOfRows={25}
                emptyState={() => null}
              />
            )}
          </div>
          <div style={{ marginTop: 32 }}>
            <h3>Contacted Leads ({contactedLeads.length})</h3>
            {contactedLeads.length === 0 ? (
              <EmptyStudents
                title="Leads"
                caption="No contacted leads."
                IconComp={() => (
                  <PermContactCalendarOutlinedIcon fontSize="large" />
                )}
                removeButton
              />
            ) : (
              <EnhancedTable
                rows={contactedLeads.map(lead => (
                  <LeadTableItem
                    lead={lead}
                    key={lead.id}
                    setUnviewedLeadCount={setUnviewedLeadCount}
                    refetch={refetch}
                    studentRefetch={studentRefetch}
                    showRelativeTime
                  />
                ))}
                columns={[
                  {
                    id: 'name',
                    label: 'Name',
                    align: 'left',
                    width: isTablet ? '80%' : '30%',
                    mobile: true,
                    sortable: true,
                    sortComparator: sortByFullName,
                  },
                  {
                    id: 'interest',
                    label: 'Interest',
                    align: 'left',
                    width: '30%',
                    mobile: false,
                    sortable: false,
                  },
                  {
                    id: 'submitted',
                    label: 'Submitted',
                    align: 'left',
                    width: '20%',
                    mobile: false,
                    sortable: true,
                    sortComparator: sortByCreatedOn,
                  },
                  {
                    id: 'actions',
                    label: '',
                    align: 'right',
                    width: '20%',
                    mobile: true,
                    sortable: false,
                  },
                ]}
                label="Contacted Leads"
                numberOfRows={25}
                emptyState={() => null}
              />
            )}
          </div>
        </TableContainer>

        <UpgradeSuccessModal
          isOpen={showSuccessModal}
          onClose={handleDismiss}
        />
      </>
    )
  }

  return (
    <>
      <TableContainer className={classes.containerStyles}>
        {loading && <LinearProgress color="secondary" />}
        <DisclaimerWithTwoCta
          icon={<InfoOutlinedIcon style={{ color: '#554427' }} />}
          description="Enjoy the benefits of your free account with up to two leads. Want more? Upgrade now to receive unlimited coaching requests."
          leftCta={
            <Button
              variant="text"
              color="primary"
              onClick={handleUpgradeClick}
              style={{ paddingLeft: 0 }}
            >
              Upgrade Now
            </Button>
          }
          rightCta={
            <Button
              variant="text"
              color="primary"
              onClick={() => {
                setShowDisclaimer(false)
              }}
            >
              Got It
            </Button>
          }
          show={showDisclaimer}
        />
        <SearchBar
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          placeholder="Search Leads"
          data-testid="leads-list-search"
        />
        <EnhancedTable
          rows={rows}
          columns={columns}
          label="Leads"
          numberOfRows={25}
          emptyState={() => (
            <EmptyStudents
              title="Leads"
              caption="No matching leads."
              IconComp={() => (
                <PermContactCalendarOutlinedIcon fontSize="large" />
              )}
              removeButton
            />
          )}
        />
      </TableContainer>

      <UpgradeSuccessModal isOpen={showSuccessModal} onClose={handleDismiss} />
    </>
  )
}

export default withSearch(LeadsTable)
